import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import { newsArticles, type NewsRelatedLink } from '@/mocks/newsData';
import PageHead from '@/components/seo/PageHead';
import { useLanguageContext } from '@/i18n/LanguageContext';

const SITE_URL = 'https://butler-academy.jp';

/** HTML本文からdescription用のプレーンテキストを生成(140-160字目安・metaDescription未設定記事のフォールバック)。 */
function descriptionFromBody(html: string, maxLen = 155): string {
  const plain = html
    .replace(/<[^>]+>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (plain.length <= maxLen) return plain;
  return `${plain.slice(0, maxLen).trim()}…`;
}

/** "YYYY年M月D日" を ISO "YYYY-MM-DD" へ(JSON-LD datePublished用)。 */
function jpDateToISO(date: string): string {
  const m = date.match(/(\d+)年(\d+)月(\d+)日/);
  if (!m) return '2026-06-13';
  const y = m[1];
  const mo = m[2].padStart(2, '0');
  const d = m[3].padStart(2, '0');
  return `${y}-${mo}-${d}`;
}

/** 短報など related 未設定の記事に使う既定の関連導線(実在ルートのみ)。 */
const DEFAULT_RELATED: NewsRelatedLink[] = [
  { title: '資料請求（無料）', desc: 'コース内容と日程をお届け', href: '/brochure' },
  { title: 'コース一覧・比較', desc: '3コースの違いを一覧で確認', href: '/courses' },
  { title: '講師ブログ', desc: '執事の作法やキャリアの解説', href: '/blog' },
];

function BackToTop() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setVisible(window.scrollY > 500);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (!visible) return null;

  return (
    <button
      onClick={scrollToTop}
      className="fixed bottom-8 right-8 w-10 h-10 flex items-center justify-center rounded-full border border-accent-400/40 text-accent-500 hover:bg-accent-500/10 hover:border-accent-400 transition-all duration-300 z-50 cursor-pointer"
      aria-label="ページの先頭へ戻る"
    >
      <i className="ri-arrow-up-line text-base"></i>
    </button>
  );
}

export default function NewsDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const { lang } = useLanguageContext();
  const article = newsArticles.find((a) => a.slug === slug);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    window.scrollTo({ top: 0 });
  }, [slug]);

  if (!mounted) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-accent-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!article) {
    return (
      <>
        <PageHead
          title="お知らせが見つかりません"
          description="お探しのお知らせは存在しないか、移動された可能性があります。日本執事アカデミーからの最新のお知らせ一覧はこちらからご覧いただけます。"
          path={`/news/${slug ?? ''}`}
          noindex
          lang={lang}
        />
        <Header solid />
        <main className="pt-[150px] pb-20">
          <div className="max-w-[800px] mx-auto px-8 text-center">
            <div className="w-14 h-14 mx-auto mb-6 flex items-center justify-center rounded-full border border-background-300">
              <i className="ri-file-info-line text-foreground-400 text-xl"></i>
            </div>
            <h1 className="font-heading text-xl text-foreground-900 tracking-[0.06em] mb-4">
              お知らせが見つかりません
            </h1>
            <p className="text-[15px] text-foreground-500 tracking-[0.04em] leading-relaxed mb-8">
              お探しのお知らせは存在しないか、移動された可能性があります。
            </p>
            <Link
              to="/news"
              className="inline-flex items-center gap-2 px-6 py-3 border border-background-300 text-foreground-600 hover:text-foreground-800 hover:border-foreground-400 text-[15px] tracking-[0.06em] transition-all duration-300 whitespace-nowrap"
            >
              <i className="ri-arrow-left-line text-xs"></i>
              お知らせ一覧に戻る
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const description = article.metaDescription ?? descriptionFromBody(article.body);
  const heroImageAbs = article.heroImage ? `${SITE_URL}${article.heroImage}` : `${SITE_URL}/ogp.jpg`;
  const datePublished = jpDateToISO(article.date);
  const related = article.related && article.related.length > 0 ? article.related : DEFAULT_RELATED;

  // JSON-LD: NewsArticle + BreadcrumbList (+ FAQPage: Q&Aを持つ記事のみ)。PageHead の jsonLd で出力。
  const jsonLd: object[] = [
    {
      '@context': 'https://schema.org',
      '@type': 'NewsArticle',
      headline: article.title,
      description,
      image: heroImageAbs,
      datePublished,
      dateModified: datePublished,
      author: {
        '@type': 'Organization',
        name: '日本執事アカデミー',
      },
      publisher: {
        '@type': 'Organization',
        name: '一般社団法人全日本執事協会',
      },
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': `${SITE_URL}/news/${article.slug}`,
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'ホーム', item: `${SITE_URL}/` },
        { '@type': 'ListItem', position: 2, name: 'お知らせ', item: `${SITE_URL}/news` },
        {
          '@type': 'ListItem',
          position: 3,
          name: article.title,
          item: `${SITE_URL}/news/${article.slug}`,
        },
      ],
    },
  ];

  if (article.faq && article.faq.length > 0) {
    jsonLd.push({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: article.faq.map((item) => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: { '@type': 'Answer', text: item.answer },
      })),
    });
  }

  const isRich = Boolean(article.heroImage || article.overview || article.faq);

  return (
    <>
      <PageHead
        title={article.title}
        description={description}
        path={`/news/${article.slug}`}
        keywords={article.keywords?.join(', ')}
        ogImage={article.heroImage}
        jsonLd={jsonLd}
        lang={lang}
      />

      <Header solid />
      <main className="pt-[150px]">
        <div className="max-w-[1600px] mx-auto px-8 md:px-12 lg:px-16 pb-20">

          {/* Article Header */}
          <div className="mb-10 max-w-[1000px]">
            <div className="flex items-center gap-3 mb-4">
              <span className="text-[14px] text-foreground-400 tracking-[0.04em] tabular-nums">
                {article.date}
              </span>
              <span className="text-[12px] px-2 py-0.5 border border-accent-400/30 text-accent-500 tracking-[0.06em]">
                {article.categoryLabel}
              </span>
            </div>
            <h1 className="font-heading text-2xl md:text-3xl text-foreground-900 tracking-[0.06em] leading-snug mb-6">
              {article.title}
            </h1>
            <div className="w-12 h-[1px] bg-accent-500/50"></div>
          </div>
          {/* Breadcrumb */}
          <nav className="text-[14px] tracking-[0.04em] mb-8" aria-label="パンくずリスト">
            <Link to="/" className="text-foreground-400 hover:text-accent-500 transition-colors">
              ホーム
            </Link>
            <i className="ri-arrow-right-s-line text-foreground-400 mx-2 text-[12px]"></i>
            <Link to="/news" className="text-foreground-400 hover:text-accent-500 transition-colors">
              お知らせ
            </Link>
            <i className="ri-arrow-right-s-line text-foreground-400 mx-2 text-[12px]"></i>
            <span className="text-accent-600">{article.title}</span>
          </nav>

          {/* Two-column: 本文(主) + サイド情報ボックス */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
            {/* Main column */}
            <div className="lg:col-span-8">
              {/* Hero Image (1枚目) */}
              {article.heroImage && (
                <figure className="mb-10 overflow-hidden">
                  <img
                    src={article.heroImage}
                    alt={article.heroAlt ?? article.title}
                    className="w-full h-[280px] md:h-[420px] object-cover object-top"
                    loading="eager"
                  />
                </figure>
              )}

              {/* Body */}
              <div
                className="prose-custom news-body text-[16px] text-foreground-700 tracking-[0.04em] leading-loose"
                dangerouslySetInnerHTML={{ __html: article.body }}
              />

              {/* Q&A (AEO) */}
              {article.faq && article.faq.length > 0 && (
                <section className="mt-14 border-t border-background-200/60 pt-10" aria-label="よくある質問">
                  <p className="text-accent-600 text-[12px] tracking-[0.2em] mb-2 font-label">Q&amp;A</p>
                  <h2 className="font-heading text-lg md:text-xl text-foreground-900 tracking-[0.06em] mb-6">
                    このお知らせに関するよくある質問
                  </h2>
                  <div className="space-y-4">
                    {article.faq.map((item, idx) => (
                      <div key={idx} className="border border-background-200/70 p-5 md:p-6 bg-background-50">
                        <h3 className="font-heading text-[16px] text-foreground-900 tracking-[0.05em] mb-2 flex gap-2">
                          <span className="text-accent-600">Q.</span>
                          <span>{item.question}</span>
                        </h3>
                        <p className="text-[15px] text-foreground-700 leading-relaxed tracking-[0.03em] pl-6">
                          {item.answer}
                        </p>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* Back Link */}
              <div className="mt-14 pt-8 border-t border-background-200/60">
                <Link
                  to="/news"
                  className="inline-flex items-center gap-2 text-accent-500 hover:text-accent-400 text-[15px] tracking-[0.06em] transition-colors whitespace-nowrap"
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-left-line text-xs"></i>
                  </div>
                  お知らせ一覧に戻る
                </Link>
              </div>
            </div>

            {/* Sidebar */}
            <aside className="lg:col-span-4">
              <div className="lg:sticky lg:top-[120px] space-y-8">
                {/* 開校概要 情報ボックス */}
                {article.overview && (
                  <div className="border border-background-200/80 bg-background-50">
                    <div className="bg-primary-900 px-5 py-3">
                      <h2 className="font-heading text-[15px] text-accent-400 tracking-[0.08em]">
                        {article.overview.heading}
                      </h2>
                    </div>
                    <dl className="divide-y divide-background-200/60">
                      {article.overview.rows.map((row, idx) => (
                        <div key={idx} className="px-5 py-3">
                          <dt className="text-[12px] text-foreground-400 tracking-[0.06em] font-label mb-1">
                            {row.label}
                          </dt>
                          <dd className="text-[15px] text-foreground-800 tracking-[0.03em] leading-relaxed">
                            {row.value}
                          </dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                )}

                {/* 記事内画像(2枚目) */}
                {article.figures && article.figures.length > 0 && (
                  <figure className="overflow-hidden border border-background-200/60">
                    <img
                      src={article.figures[0].src}
                      alt={article.figures[0].alt}
                      className="w-full h-[220px] object-cover object-top"
                      loading="lazy"
                    />
                    {article.figures[0].caption && (
                      <figcaption className="px-4 py-3 text-[12px] text-foreground-500 tracking-[0.03em] leading-relaxed bg-background-50">
                        {article.figures[0].caption}
                      </figcaption>
                    )}
                  </figure>
                )}

                {/* CTA: 資料請求 */}
                <div className="border border-accent-400/40 bg-accent-50/40 p-6">
                  <p className="font-heading text-[16px] text-foreground-900 tracking-[0.05em] mb-2">
                    資料請求（無料）
                  </p>
                  <p className="text-[13px] text-foreground-600 leading-relaxed tracking-[0.03em] mb-4">
                    コース内容・カリキュラム・料金をまとめた資料をPDFでお届けします。
                  </p>
                  <Link
                    to="/brochure"
                    className="inline-flex items-center justify-center gap-2 w-full px-5 py-3 bg-accent-500 text-primary-900 text-[14px] tracking-[0.08em] font-medium hover:bg-accent-400 transition-colors whitespace-nowrap"
                  >
                    資料を請求する
                    <i className="ri-arrow-right-line text-sm"></i>
                  </Link>
                  <Link
                    to="/contact"
                    className="inline-flex items-center justify-center gap-2 w-full px-5 py-2.5 mt-3 border border-background-300 text-foreground-600 text-[14px] tracking-[0.06em] hover:text-foreground-900 hover:border-foreground-400 transition-colors whitespace-nowrap"
                  >
                    お問い合わせ
                  </Link>
                </div>

                {/* 関連リンク */}
                <div>
                  <h2 className="font-heading text-[15px] text-foreground-900 tracking-[0.08em] mb-4 pb-2 border-b border-background-200/60">
                    関連情報
                  </h2>
                  <ul className="space-y-3">
                    {related.map((link, idx) => (
                      <li key={idx}>
                        <Link
                          to={link.href}
                          className="group flex items-start gap-3 p-3 border border-background-200/60 hover:border-accent-400/50 hover:bg-background-100/60 transition-all duration-300"
                        >
                          <i className="ri-arrow-right-s-line text-accent-400 text-base mt-0.5 flex-shrink-0"></i>
                          <span className="min-w-0">
                            <span className="block text-[14px] text-foreground-800 group-hover:text-accent-600 tracking-[0.03em] transition-colors">
                              {link.title}
                            </span>
                            {link.desc && (
                              <span className="block text-[12px] text-foreground-500 tracking-[0.02em] mt-0.5 leading-relaxed">
                                {link.desc}
                              </span>
                            )}
                          </span>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </aside>
          </div>

          {!isRich && (
            <p className="sr-only">{description}</p>
          )}
        </div>

        <footer className="py-12 bg-background-100/50 text-center">
          <p className="text-[13px] text-foreground-400 tracking-[0.04em]">
            最終更新: {article.date}
          </p>
        </footer>

        <BackToTop />
      </main>
      <Footer />
    </>
  );
}
